import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { map, mergeAll, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { empty, from, Observable, of } from 'rxjs';
import { AngularFirestore, Query } from '@angular/fire/firestore';

import { UserDetail } from '@app/interface/auth.user';
import { UserGroupFormModel } from '@app/formModels';

import { FirebaseAuthService } from './firebaseauth.service';
import { StudiesService } from './studies.service';
import * as firebase from 'firebase';
import { GroupMemberModel } from '@app/interface/groupMember.model';
import { MessageService } from 'primeng/api';
@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  constructor(
    private store: AngularFirestore,
    private afAuth: AngularFireAuth,
    private authService: FirebaseAuthService,
    private studiesService: StudiesService,
    private messageService: MessageService
  ) {}

  public createGroup(userGroup: UserGroupFormModel): Observable<any> {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return empty();
        }
        userGroup.uuid = this.store.createId();
        userGroup.createdBy = user.role;
        userGroup.createdByUserId = user.uuid;
        userGroup.notification_key = null;
        return this.store.collection('user-groups').doc(userGroup.uuid).set(userGroup, { merge: true });
      })
    );
  }

  public updateGroup(userGroup: UserGroupFormModel): Observable<any> {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return empty();
        }
        userGroup.createdBy = user.role;
        userGroup.updatedByUserId = user.uuid;
        return this.store.collection('user-groups').doc(userGroup.uuid).set(userGroup, { merge: true });
      })
    );
  }

  public getUserGroups(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return of([]);
        }
        return this.store
          .collection('user-groups', (ref) => {
            let query: Query = ref;
            return query.where('isDelete', '==', false).where('studyId', '==', studyId).orderBy('createdAt', 'desc');
          })
          .valueChanges();
      })
    );
  }

  public deleteGroup(id: string): Observable<any> {
    return from(this.store.collection('user-groups').doc(id).update({ isDelete: true }));
  }

  public getGroupMembersByGroupId(groupId: string): Observable<any> {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return of([]);
        }
        return this.store
          .collection('group-members', (ref) => {
            let query: Query = ref;
            return query.where('isDelete', '==', false).where('groupId', '==', groupId);
          })
          .valueChanges();
      })
    );
  }

  public getDefaultGroupMembers(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.authService.getUser().pipe(
      mergeMap((user: UserDetail) => {
        if (!user) {
          return of([]);
        }
        return this.getMemberByGroup(studyId, user);
      })
    );
  }

  private getMemberByGroup(studyId: string, user: UserDetail): Observable<any> {
    return this.store
      .collection('user-groups', (ref) => {
        let query: Query = ref;
        return query
          .where('isDelete', '==', false)
          .where('isDefault', '==', true)
          .where('studyId', '==', studyId)
          .where('createdBy', '==', user.role);
      })
      .valueChanges()
      .pipe(
        mergeMap((item: UserGroupFormModel[]) => {
          let group = item.pop();
          return this.getGroupMembersByGroupId(group.uuid);
        })
      );
  }

  public addMembers(members: GroupMemberModel[], groupId: string): Observable<any> {
    return this.authService.getUser().pipe(
      mergeMap((user: UserDetail) => {
        if (!user) {
          return empty();
        }
        let response = this.saveMembers(members, groupId, user);
        return from(response);
      })
    );
  }

  async saveMembers(members: GroupMemberModel[], groupId: string, user: UserDetail): Promise<any> {
    let result = await members.map(async (item) => {
      let isExists = await this.checkUserExist(item.userId, groupId);
      if (!isExists) {
        item.uuid = this.store.createId();
        item.groupId = groupId;
        item.groupRef = this.store.collection('user-groups').doc().ref;
        item.createdAt = firebase.default.firestore.Timestamp.now();
        item.updatedAt = firebase.default.firestore.Timestamp.now();
        item.createdBy = user.role;
        item.createdByUserId = user.uuid;
        item.studyId = this.studiesService.getStudyId();
        return await firebase.default.firestore().collection('group-members').doc(item.uuid).set(item);
      }
    });
    return result;
  }

  public deleteGroupMember(id: string): Observable<any> {
    return from(this.store.collection('group-members').doc(id).delete());
  }

  private async checkUserExist(userId: string, groupId: string): Promise<boolean> {
    let member = await this.store
      .collection('group-members')
      .ref.where('userId', '==', userId)
      .where('groupId', '==', groupId)
      .get();
    let doc = member.docs;
    return doc.length > 0 ? true : false;
  }

  public getDefaultStudyGroup(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return of([]);
        }
        return this.store
          .collection('user-groups', (ref) => {
            return ref.where('studyId', '==', studyId).where('isDefault', '==', true);
          })
          .valueChanges()
          .pipe(map((item: UserGroupFormModel[]) => item.pop()));
      })
    );
  }

  public getEnrolledGroupMember() {
    let studyId = this.studiesService.getStudyId();
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (!user) {
          return of([]);
        }
        return this.store
          .collection('user-groups', (ref) => {
            let query: Query = ref;
            return query.where('isDelete', '==', false).where('isDefault', '==', true).where('studyId', '==', studyId);
          })
          .valueChanges()
          .pipe(
            mergeMap((item: UserGroupFormModel[]) => {
              let group = item.pop();
              return this.store
                .collection('group-members', (ref) => {
                  let query: Query = ref;
                  return query.where('isDelete', '==', false).where('groupId', '==', group.uuid);
                })
                .valueChanges();
            })
          );
      })
    );
  }
}
